import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  InvitationService,
  Invitation,
  InviteUsersData,
  InvitationDetails,
  AxiosErrorResponse,
  OrganizationMember,
  PaginationMeta,
} from "@/lib/api/services/invitationService";
import { OrganizationService } from "@/lib/api/services/organizationService";

export interface OrganizationMembersFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  role?: string;
}

export interface InvitationState {
  invitations: Invitation[];
  userInvitations: Invitation[];
  organizationMembers: OrganizationMember[];
  organizationMembersPagination: PaginationMeta | null;
  currentInvitation: Invitation | null;
  currentInvitationDetails: InvitationDetails | null;
  isLoading: boolean;
  error: string | null;
  successMessage: string | null;
}

const defaultInitialState: InvitationState = {
  invitations: [],
  userInvitations: [],
  organizationMembers: [],
  organizationMembersPagination: null,
  currentInvitation: null,
  currentInvitationDetails: null,
  isLoading: false,
  error: null,
  successMessage: null,
};
const initialState: InvitationState = defaultInitialState;

interface ErrorResponse {
  message: string;
  code: string;
  [key: string]: unknown;
}

const handleApiError = (error: unknown): ErrorResponse => {
  const axiosError = error as AxiosErrorResponse;

  if (axiosError.response?.data) {
    const data = axiosError.response.data;
    const messageValue = Array.isArray(data.message)
      ? data.message[0]
      : data.message || "An unexpected error occurred";
    const codeValue = data.code || "UNKNOWN_ERROR";

    return {
      message: messageValue,
      code: codeValue,
    };
  }

  if (typeof error === "object" && error !== null) {
    const errorObj = error as Record<string, unknown>;
    if (errorObj.code && errorObj.message) {
      const message = Array.isArray(errorObj.message)
        ? (errorObj.message[0] as string)
        : (errorObj.message as string);
      return {
        message,
        code: errorObj.code as string,
      };
    }
  }

  return {
    message: (axiosError.message as string) || "An unexpected error occurred",
    code: "UNKNOWN_ERROR",
  };
};

export const getOrganizationMembers = createAsyncThunk(
  "invitation/getOrganizationMembers",
  async (
    {
      organizationId,
      page,
      limit,
      search,
      status,
      role,
    }: {
      organizationId: number;
    } & OrganizationMembersFilters,
    { rejectWithValue },
  ) => {
    try {
      return await InvitationService.getOrganizationMembers(
        organizationId,
        page,
        limit,
        search,
        status,
        role,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const inviteUsersToSite = createAsyncThunk(
  "invitation/inviteUsersToSite",
  async (
    {
      organizationId,
      siteId,
      inviteData,
    }: {
      organizationId: number;
      siteId: number;
      inviteData: InviteUsersData;
    },
    { rejectWithValue },
  ) => {
    try {
      return await InvitationService.inviteUsersToSite(
        organizationId,
        siteId,
        inviteData,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getSiteMembers = createAsyncThunk(
  "invitation/getSiteMembers",
  async (
    { organizationId, siteId }: { organizationId: number; siteId: number },
    { rejectWithValue },
  ) => {
    try {
      return await InvitationService.getSiteMembers(organizationId, siteId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateMemberRole = createAsyncThunk(
  "invitation/updateMemberRole",
  async (
    {
      organizationId,
      siteId,
      memberId,
      role,
    }: {
      organizationId: number;
      siteId: number;
      memberId: number;
      role: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await InvitationService.updateMemberRole(
        organizationId,
        siteId,
        memberId,
        { role },
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateMemberStatus = createAsyncThunk(
  "invitation/updateMemberStatus",
  async (
    {
      organizationId,
      siteId,
      memberId,
      status,
    }: {
      organizationId: number;
      siteId: number;
      memberId: number;
      status: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await InvitationService.updateMemberStatus(
        organizationId,
        siteId,
        memberId,
        { status },
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const removeMember = createAsyncThunk(
  "invitation/removeMember",
  async (
    {
      organizationId,
      siteId,
      memberId,
    }: {
      organizationId: number;
      siteId: number;
      memberId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      return await InvitationService.removeMember(
        organizationId,
        siteId,
        memberId,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const acceptInvitation = createAsyncThunk(
  "invitation/acceptInvitation",
  async ({ invitationId }: { invitationId: number }, { rejectWithValue }) => {
    try {
      return await InvitationService.acceptInvitation(invitationId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const rejectInvitation = createAsyncThunk(
  "invitation/rejectInvitation",
  async ({ invitationId }: { invitationId: number }, { rejectWithValue }) => {
    try {
      return await InvitationService.rejectInvitation(invitationId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getUserInvitations = createAsyncThunk(
  "invitation/getUserInvitations",
  async (_, { rejectWithValue }) => {
    try {
      return await InvitationService.getUserInvitations();
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getInvitationByToken = createAsyncThunk(
  "invitation/getInvitationByToken",
  async (
    { invitationToken }: { invitationToken: string },
    { rejectWithValue },
  ) => {
    try {
      return await InvitationService.getInvitationByToken(invitationToken);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const verifyInvitationForUser = createAsyncThunk(
  "invitation/verifyInvitationForUser",
  async (
    { invitationToken }: { invitationToken: string },
    { rejectWithValue },
  ) => {
    try {
      return await InvitationService.verifyInvitationForUser(invitationToken);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const acceptInvitationByToken = createAsyncThunk(
  "invitation/acceptInvitationByToken",
  async (
    { invitationToken }: { invitationToken: string },
    { rejectWithValue },
  ) => {
    try {
      return await InvitationService.acceptInvitationByToken(invitationToken);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const rejectInvitationByToken = createAsyncThunk(
  "invitation/rejectInvitationByToken",
  async (
    { invitationToken }: { invitationToken: string },
    { rejectWithValue },
  ) => {
    try {
      return await InvitationService.rejectInvitationByToken(invitationToken);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const removeOrganizationMember = createAsyncThunk(
  "invitation/removeOrganizationMember",
  async (
    {
      organizationId,
      memberId,
    }: {
      organizationId: number;
      memberId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      return await OrganizationService.removeOrganizationMember(
        organizationId,
        memberId,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const resendOrganizationMemberInvitation = createAsyncThunk(
  "invitation/resendOrganizationMemberInvitation",
  async (
    {
      organizationId,
      memberId,
    }: {
      organizationId: number;
      memberId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      return await OrganizationService.resendMemberInvitation(
        organizationId,
        memberId,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

const invitationSlice = createSlice({
  name: "invitation",
  initialState,
  reducers: {
    setCurrentInvitation: (state, action: PayloadAction<Invitation | null>) => {
      state.currentInvitation = action.payload;
    },
    setCurrentInvitationDetails: (
      state,
      action: PayloadAction<InvitationDetails | null>,
    ) => {
      state.currentInvitationDetails = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    resetInvitations: (state) => {
      state.invitations = [];
      state.userInvitations = [];
      state.currentInvitation = null;
      state.currentInvitationDetails = null;
      state.isLoading = false;
      state.error = null;
      state.successMessage = null;
    },
    clearOrganizationMembers: (state) => {
      state.organizationMembers = [];
      state.organizationMembersPagination = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(inviteUsersToSite.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(inviteUsersToSite.fulfilled, (state, action) => {
        state.isLoading = false;
        state.invitations = action.payload.invitations;
        state.successMessage = action.payload.message;
      })
      .addCase(inviteUsersToSite.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as ErrorResponse)?.message ||
          "Failed to invite users";
      })
      .addCase(getSiteMembers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSiteMembers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.invitations = action.payload.members;
      })
      .addCase(getSiteMembers.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as ErrorResponse)?.message || "Failed to get members";
      })
      .addCase(updateMemberRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateMemberRole.fulfilled, (state, action) => {
        state.isLoading = false;
        const idx = state.invitations.findIndex(
          (inv: Invitation) => inv.id === action.payload.member.id,
        );
        if (idx !== -1) {
          state.invitations[idx] = action.payload.member;
        }
        state.successMessage = action.payload.message;
      })
      .addCase(updateMemberRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as ErrorResponse)?.message || "Failed to update role";
      })
      .addCase(updateMemberStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateMemberStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        const idx = state.invitations.findIndex(
          (inv: Invitation) => inv.id === action.payload.member.id,
        );
        if (idx !== -1) {
          state.invitations[idx] = action.payload.member;
        }
        state.successMessage = action.payload.message;
      })
      .addCase(updateMemberStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as ErrorResponse)?.message ||
          "Failed to update status";
      })
      .addCase(removeMember.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeMember.fulfilled, (state, action) => {
        state.isLoading = false;
        state.invitations = state.invitations.filter(
          (inv: Invitation) => inv.id !== action.meta.arg.memberId,
        );
        state.successMessage = action.payload.message;
      })
      .addCase(removeMember.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as ErrorResponse)?.message ||
          "Failed to remove member";
      })
      .addCase(acceptInvitation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(acceptInvitation.fulfilled, (state, action) => {
        state.isLoading = false;
        const idx = state.userInvitations.findIndex(
          (inv: Invitation) => inv.id === action.payload.invitation.id,
        );
        if (idx !== -1) {
          state.userInvitations[idx] = action.payload.invitation;
        }
        state.successMessage = action.payload.message;
      })
      .addCase(acceptInvitation.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as ErrorResponse)?.message ||
          "Failed to accept invitation";
      })
      .addCase(rejectInvitation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(rejectInvitation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userInvitations = state.userInvitations.filter(
          (inv: Invitation) => inv.id !== action.meta.arg.invitationId,
        );
        state.successMessage = action.payload.message;
      })
      .addCase(rejectInvitation.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as ErrorResponse)?.message ||
          "Failed to reject invitation";
      })
      .addCase(getUserInvitations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserInvitations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userInvitations = action.payload.invitations;
      })
      .addCase(getUserInvitations.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as ErrorResponse)?.message ||
          "Failed to get invitations";
      })
      .addCase(getInvitationByToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getInvitationByToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentInvitationDetails = action.payload;
      })
      .addCase(getInvitationByToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as ErrorResponse)?.message ||
          "Failed to load invitation";
      })
      .addCase(verifyInvitationForUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyInvitationForUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentInvitationDetails = action.payload;
      })
      .addCase(verifyInvitationForUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as ErrorResponse)?.message ||
          "Failed to verify invitation";
      })
      .addCase(acceptInvitationByToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(acceptInvitationByToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(acceptInvitationByToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as ErrorResponse)?.message ||
          "Failed to accept invitation";
      })
      .addCase(rejectInvitationByToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(rejectInvitationByToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(rejectInvitationByToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as ErrorResponse)?.message ||
          "Failed to reject invitation";
      })
      .addCase(getOrganizationMembers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOrganizationMembers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizationMembers = action.payload.members;
        state.organizationMembersPagination = action.payload.pagination;
      })
      .addCase(getOrganizationMembers.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as ErrorResponse)?.message ||
          "Failed to get organization members";
      })

      .addCase(removeOrganizationMember.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeOrganizationMember.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successMessage = action.payload.message;
        state.organizationMembers = state.organizationMembers.filter(
          (m) => m.id !== action.meta.arg.memberId,
        );
        if (state.organizationMembersPagination) {
          state.organizationMembersPagination.total -= 1;
        }
      })
      .addCase(removeOrganizationMember.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as ErrorResponse)?.message ||
          "Failed to remove member";
      })
      .addCase(resendOrganizationMemberInvitation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        resendOrganizationMemberInvitation.fulfilled,
        (state, action) => {
          state.isLoading = false;
          state.successMessage = action.payload.message;
        },
      )
      .addCase(resendOrganizationMemberInvitation.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as ErrorResponse)?.message ||
          "Failed to resend invitation";
      });
  },
});

export const {
  setCurrentInvitation,
  setCurrentInvitationDetails,
  clearError,
  clearSuccessMessage,
  resetInvitations,
  clearOrganizationMembers,
} = invitationSlice.actions;

export default invitationSlice.reducer;
